import { Context, Plugin } from '@nuxt/types'
import { associations } from '~/plugins/mixpanel/associations'
import * as events from '~/plugins/mixpanel/events'
import * as props from '~/plugins/mixpanel/props'
import values from '~/plugins/mixpanel/values'
import { Feature } from '~/plugins/feature-flags'

const mixpanel = require('mixpanel-browser')
const PRODUCTION = 'production'

export type EventName = keyof typeof events;
export type EventValue = (typeof events)[EventName]

export type PropName = keyof typeof props;

interface SourceDefineProps {
  scholarshipId?: number,
  scholarship?: Scholarship.General,
  requirementType?: string;
  requirementId?: number
  props?:any
}

export interface MixpanelService {
  track: (event: string, props?: any) => void,
  trackExperimentStart: (experimentName: string, featureFlag: Feature) => void,
  identifyUser: (id: number) => void,
  updateUserInfo: (props: any) => void,
  fillProps: (event: string, opts?: SourceDefineProps) => any,
  events: { [key: string]: string },
  props: { [key: string]: string }
}

interface Sources {
  route: any;
  $accessor: any;
  $auth: any;
  store: any;
  scholarship?: any;
  requirement?: any;
}

declare module 'vue/types/vue' {
  interface Vue {
    $mixpanel: MixpanelService
  }
}

declare module '@nuxt/types' {
  // inside asyncData, fetch, plugins, middleware, nuxtServerInit
  interface NuxtAppOptions {
    $mixpanel: MixpanelService;
  }
  // context
  interface Context {
    $mixpanel: MixpanelService;
  }
}

declare module 'vuex/types/index' {
  // Vuex stores
  /* eslint-disable */
  interface Store<S> {
    $mixpanel: MixpanelService;
  }
}

const defaultPropNames = [
  props.ACCOUNT,
  props.IS_MEMBER,
  props.AFF_ID,
  props.IS_MOBILE,
  props.FSET,
  props.SRV,
  props.COUNTRY,
  props.URL,
  props.MEMBERSHIP_STATUS,
]

const fillProps = (props: (keyof typeof values)[], resources) => props.reduce((acc, prop) => {
  if (!values[prop]) return acc
  const value = values[prop](resources)
  if (value === undefined || value === null) return acc
  acc[prop] = value
  return acc
}, {})

const mixpanelPlugin: Plugin = (context: Context, inject) => {
  let enabled = false
  
  if (context.$config.mixpanel.apikey) {
    mixpanel.init(context.$config.mixpanel.apikey, {
      persistence: 'localStorage',
      ignore_dnt: true,
    })
    enabled = true;
  }

  const mixpanelPlugin = {
    track (event, props) {
      props = {
        ...props,
        ...fillProps(defaultPropNames as (keyof typeof values)[], { $accessor: context.$accessor, $auth: context.$auth }),
        'Feature flags': context.$featureFlags?.getAllForTracking()
      }

      if (enabled) {
        mixpanel.track(event, props)
      }
      if (context.$config.NODE_ENV !== PRODUCTION) {
        console.log('MIXPANEL DEBUG: mixpanel track', event, props)
      }
    },
    updateUserInfo (props) {
      if (enabled) {
        mixpanel.people.set(props);
      }
      if (context.$config.NODE_ENV !== PRODUCTION) {
        console.log('MIXPANEL DEBUG: update account info', props);
      }
    },
    identifyUser (id) {
      if (enabled) {
        mixpanel.identify(id);
      }
      if (context.$config.NODE_ENV !== PRODUCTION) {
        console.log(`User with id: ${id} identified`)
      }
    },
    trackExperimentStart(experimentName: string, featureFlag: Feature) {
      const accountId = context.$auth?.user?.id
      if (!accountId) throw new Error('Account ID not found')

      const cookieName = 'sowl-experiments-started'
      const started: Record<string, Record<string, boolean>> = context.app.$cookies?.get(cookieName) || {}

      if (started[accountId]?.[experimentName]) return console.log(`Experiment "${experimentName}" already started for account ${accountId}.`)

      const variant = context.$featureFlags?.getVariant(featureFlag)

      mixpanelPlugin.track('$experiment_started', {
        'Experiment name': experimentName,
        'Variant name': variant?.name || 'disabled',
      })

      if (!started[accountId]) {
        started[accountId] = {}
      }
      started[accountId][experimentName] = true
      context.app.$cookies?.set(cookieName, started, { path: '/' })
    },
    fillProps (event: keyof typeof events, opts) {
      if (!opts) { opts = {} }

      const { route, $accessor, store } = context
      const sources: Sources = { route, $accessor, $auth: context.$auth, store, scholarship: {}, requirement: {} }
      const details = $accessor.details.scholarships[route.params.cardId]

      if (opts.scholarship) {
        sources.scholarship = opts.scholarship
      }

      if (!opts.scholarship && opts.scholarshipId) {
        sources.scholarship = $accessor.cardList.getListItem({
          storeName: route.params.tabName === 'submitted'
            ? 'applicationList'
            : 'scholarshipList',
          id: opts.scholarshipId
        })
      }

      if (details) {
        const detailsScholarship = 'scholarship' in details
          ? details.scholarship
          : {}

        sources.scholarship = sources.scholarship
          ? { ...detailsScholarship, ...sources.scholarship }
          : detailsScholarship
      }

      if (opts.scholarshipId && opts.requirementId && opts.requirementType) {
        sources.requirement = $accessor.requirements.getRequirement({
          scholarshipId: opts.scholarshipId,
          requirementId: opts.requirementId,
          requirementType: opts.requirementType
        })
      }

      return {...fillProps(associations[event] as (keyof typeof values)[], sources), ...opts.props}
    },
    events,
    props,

  } as MixpanelService

  context.$mixpanel = mixpanelPlugin
  inject('mixpanel', mixpanelPlugin)
}

export default mixpanelPlugin

